import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { T, A } from 'TOMA';
import useApiContext from '../../hooks/useApiContext';
import FormInformationQuestion from './FormInformationQuestion';
import s from '../../pages/FormInformation/FormInformation.module.scss';
import { iFormQuestion } from '../../services/Event/Event.type';

interface Props {
  eventId: string;
  canEdit: boolean;
  hideTitle?: boolean;
  // filled with a function that saves every added/changed question
  saveRef?: React.MutableRefObject<(() => Promise<void>) | null>;
}

interface QuestionItem extends iFormQuestion {
  uid: number;
}

// Event-specific questions (3 answer options each), editable from the Event Edit page.
function EventQuestions({
  eventId,
  canEdit,
  saveRef,
  hideTitle = false,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const { eventRepository } = useApiContext();
  const [questions, setQuestions] = useState<QuestionItem[]>([]);
  const [newUids, setNewUids] = useState<number[]>([]);
  const nextUid = useRef(0);
  const savers = useRef(new Map<number, () => Promise<void>>());

  useEffect(() => {
    if (!saveRef) return;
    saveRef.current = async () => {
      await Promise.all(
        Array.from(savers.current.values()).map(async (f) => await f())
      );
    };
    return () => {
      saveRef.current = null;
    };
  }, [saveRef]);

  useEffect(() => {
    eventRepository
      .getFormInformation(eventId)
      .then((list) =>
        setQuestions(
          (Array.isArray(list) ? list : []).map((q) => ({
            ...q,
            uid: nextUid.current++,
          }))
        )
      )
      .catch(() => setQuestions([]));
  }, [eventId]);

  const addQuestion = (): void => {
    const uid = nextUid.current++;
    setNewUids((ids) => [...ids, uid]);
    setQuestions((list) => [
      ...list,
      { id: 0, answers: ['', '', ''], question: '', required: false, uid },
    ]);
  };

  return (
    <>
      {!hideTitle && (
        <>
          <A.Title
            as="h2"
            fontSize="24"
            textAlign="center"
            mt="20"
            mb="20"
            fw="black"
          >
            {t('event:questions')}
          </A.Title>
          <A.Line mb="20" />
        </>
      )}
      {questions.map((q) => (
        <FormInformationQuestion
          key={q.uid}
          id={q.id}
          question={q.question}
          answers={q.answers}
          required={!!q.required}
          eventId={eventId}
          canEdit={canEdit}
          card
          registerSave={(fn) =>
            fn ? savers.current.set(q.uid, fn) : savers.current.delete(q.uid)
          }
          defaultOpen={newUids.includes(q.uid)}
          onDelete={() =>
            setQuestions((list) => list.filter((i) => i.uid !== q.uid))
          }
        />
      ))}
      <div className={s.new_question}>
        <button
          type="button"
          onClick={addQuestion}
          disabled={!canEdit}
          className={s.new_question_button}
        >
          <A.Icon icon="plus-circle"></A.Icon>
          {t('home:formInformation.add_new_question')}
        </button>
      </div>
    </>
  );
}

export default EventQuestions;
